import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, ShieldCheck, BarChart3, Package, Users, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { sendVerificationCode, verifyEmail } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';

const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
};

const VerifyEmail = () => {
  const { user, loading, markEmailVerified } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  // Redirect if not logged in or already verified
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
    } else if (!loading && user?.email_verified) {
      navigate(user.role === 'superadmin' ? '/superadmin' : '/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const maskedEmail = user?.email
    ? user.email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + '*'.repeat(Math.min(b.length, 6)) + c)
    : '';

  const handleChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...code];
      digits.forEach((d, i) => {
        if (index + i < 6) newCode[index + i] = d;
      });
      setCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      // Auto-submit if all 6 digits filled
      if (newCode.every(d => d !== '')) {
        handleVerify(newCode.join(''));
      }
      return;
    }

    const digit = value.replace(/\D/g, '');
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && newCode.every(d => d !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      pasted.split('').forEach((d, i) => { newCode[i] = d; });
      setCode(newCode);
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
      if (newCode.every(d => d !== '')) {
        handleVerify(newCode.join(''));
      }
    }
  };

  const handleVerify = async (codeStr) => {
    if (verifying) return;
    const finalCode = codeStr || code.join('');
    if (finalCode.length !== 6) {
      toast.error('Ingresa el codigo de 6 digitos');
      return;
    }

    try {
      setVerifying(true);
      await verifyEmail(user.email, finalCode);
      markEmailVerified();
      toast.success('Correo verificado exitosamente');
      navigate(user.role === 'superadmin' ? '/superadmin' : '/dashboard', { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Codigo incorrecto'));
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resending || countdown > 0) return;
    try {
      setResending(true);
      await sendVerificationCode(user.email);
      setCountdown(60);
      toast.success('Codigo reenviado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al reenviar codigo'));
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  if (!user) return null;

  const features = [
    { icon: BarChart3, title: 'Reportes en tiempo real', desc: 'Ventas, inventario y mas' },
    { icon: Package, title: 'Gestion de productos', desc: 'Catalogo completo con imagenes' },
    { icon: Users, title: 'Control de sucursales', desc: 'Cajeros, repartidores y mas' },
    { icon: Shield, title: 'Punto de venta', desc: 'POS profesional integrado' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center py-8 px-4">
        <div className="w-full max-w-4xl mb-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver al marketplace
          </Link>
        </div>
        <div className="w-full max-w-4xl flex rounded-2xl overflow-hidden shadow-sm bg-white">
          {/* Left panel */}
          <div className="hidden lg:flex flex-col justify-center w-[380px] p-10" style={{ backgroundColor: COLORS.navyBlue }}>
            <h2 className="text-2xl font-bold text-white mb-1">Panel de Negocios</h2>
            <p className="text-blue-200 text-sm mb-8">Administra tu negocio desde un solo lugar</p>
            <div className="space-y-5">
              {features.map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">{f.title}</h3>
                    <p className="text-blue-200 text-xs mt-0.5">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right panel */}
          <div className="flex-1 p-6 sm:p-10">
            <div className="max-w-sm mx-auto">
              <div className="flex flex-col items-center mb-8">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: COLORS.navyBlue + '10' }}>
                  <ShieldCheck className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
                </div>
                <h2 className="text-2xl font-bold text-center" style={{ color: COLORS.darkSlate }}>
                  Verifica tu correo
                </h2>
                <p className="mt-2 text-sm text-slate-500 text-center">
                  Enviamos un codigo de 6 digitos a
                </p>
                <p className="flex items-center gap-1.5 mt-1 text-sm font-medium" style={{ color: COLORS.navyBlue }}>
                  <Mail className="w-4 h-4" />
                  {maskedEmail}
                </p>
              </div>

              {/* Code inputs */}
              <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-200 rounded-xl focus:outline-none focus:border-transparent focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': COLORS.navyBlue + '60', color: COLORS.darkSlate }}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              {/* Verify button */}
              <button
                onClick={() => handleVerify()}
                disabled={verifying || code.some(d => d === '')}
                className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
                style={{ backgroundColor: COLORS.orange }}
              >
                {verifying ? 'Verificando...' : 'Verificar Correo'}
              </button>

              {/* Resend */}
              <div className="text-center mt-5">
                <p className="text-sm text-slate-500 mb-2">
                  No recibiste el codigo?
                </p>
                <button
                  onClick={handleResend}
                  disabled={resending || countdown > 0}
                  className="text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ color: countdown > 0 ? '#94a3b8' : COLORS.orange }}
                >
                  {countdown > 0 ? `Reenviar en ${countdown}s` : resending ? 'Enviando...' : 'Reenviar codigo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
